import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiExternalLink, FiX } from 'react-icons/fi';
import { apiRequest } from '../utils/auth';
import Pagination from '../components/Pagination';
import ModelUsagePanel from '../components/ModelUsagePanel';
import GenerationOutcomeSummary, { OutcomeFields } from '../components/GenerationOutcomeSummary';
import AnalyticsHeader from '../components/AnalyticsHeader';
import AnalyticsDateControl from '../components/AnalyticsDateControl';
import MetricHelp from '../components/MetricHelp';
import AnalyticsChange, { changeDirection } from '../components/AnalyticsChange';
import {
  AnalyticsAdvancedFilters,
  AnalyticsFilterActions,
  AnalyticsFilterBar,
  AnalyticsFilterGroup,
  AnalyticsPeriodGroup,
  AnalyticsPeriodPresets,
  AnalyticsPrimaryButton,
  AnalyticsRefreshButton,
  AnalyticsSecondaryButton,
  AnalyticsSelectField,
  AnalyticsTextField,
} from '../components/AnalyticsFilterBar';
import { hktToday, recentDays, validateRange } from '../utils/periodDashboard';
import './GenerationUsagePage.css';

interface Attempt {
  attemptNumber: number;
  provider: string;
  model: string;
  modelId?: string;
  status: string;
  durationMs: number;
  errorMessage?: string;
  errorCode?: string;
  requestParams?: {
    aspectRatio?: string;
    imageSize?: string;
    numberOfImages?: number;
    inputImageCount?: number;
    allowFallbackProvider?: boolean;
    extraParams?: Array<{ key: string; value: string | number | boolean }>;
  };
}
export interface UsageRow extends OutcomeFields {
  reservationId: string;
  userId: string;
  userName: string | null;
  amount: number;
  consumedCredits: number | null;
  status: string;
  operationType: string;
  model?: string;
  generationId?: string;
  createdAt: string;
  fallback: string;
  generationLog: {
    _id: string;
    finalStatus: string;
    successfulProvider?: string;
    requestModel: string;
    requestModelId?: string;
    totalDurationMs: number;
    attempts: Attempt[];
  } | null;
}
export interface UsageData {
  rows: UsageRow[];
  summary: {
    total: number;
    confirmedCredits: number;
    reservedCredits: number;
    releasedCredits: number;
    expiredCredits: number;
    failedCredits: number;
    fallbackSuccess: number;
    fallbackFailed: number;
    unknownFallback: number;
    failedGenerations: number;
    attempt1Success: number;
    attempt2Success: number;
    attempt3Success: number;
    attempt1Credits: number;
    attempt2Credits: number;
    attempt3Credits: number;
    attempt1PaidCount: number;
    attempt2PaidCount: number;
    attempt3PaidCount: number;
    unknownAttemptCredits: number;
  };
  previous?: {
    period: { startDate: string; endDate: string };
    summary: UsageData['summary'];
  } | null;
  period: { startDate: string; endDate: string } | null;
  pagination: { page: number; pages: number; total: number; limit: number };
}
const FALLBACK_LABELS: Record<string, string> = {
  success: 'Succeeded',
  failed: 'Failed',
  skipped: 'Skipped',
  not_used: 'Not used',
  unknown: 'Unknown',
};
const FALLBACK_SIGNALS = [
  {
    key: 'fallbackSuccess',
    filter: 'success',
    label: 'Fallback succeeded',
    description: 'Operations with a successful third attempt on a fallback provider.',
  },
  {
    key: 'fallbackFailed',
    filter: 'failed',
    label: 'Fallback failed',
    description: 'Operations with a failed third attempt on a fallback provider.',
  },
  {
    key: 'unknownFallback',
    filter: 'unknown',
    label: 'Outcome unknown',
    description: 'Operations without enough evidence to classify provider fallback.',
  },
] as const;
const dateTime = (value: string) => new Date(value).toLocaleString('en-GB', { timeZone: 'Asia/Hong_Kong' });
const number = (value: number) => value.toLocaleString('en-US');

type CardDefinition = {
  key: keyof UsageData['summary'];
  label: string;
  unit: string;
  description: string;
  attempt?: number;
  countKey?: keyof UsageData['summary'];
};
const SUMMARY_CARDS: CardDefinition[] = [
  {
    key: 'confirmedCredits',
    label: 'Confirmed credits',
    unit: 'credits',
    description: 'Credits confirmed as consumed',
  },
  {
    key: 'reservedCredits',
    label: 'Reserved credits',
    unit: 'credits',
    description: 'Credits held for pending operations',
  },
  {
    key: 'failedGenerations',
    label: 'Failed after 3 attempts',
    unit: 'operations',
    description: 'Operations where attempts 1, 2 and 3 all failed and the final AI status is failed',
  },
  {
    key: 'attempt1Credits',
    countKey: 'attempt1PaidCount',
    attempt: 1,
    label: 'Attempt 1 · success',
    unit: 'credits',
    description:
      'Confirmed credits assigned to successful attempt 1; click to filter model usage and operations',
  },
  {
    key: 'attempt2Credits',
    countKey: 'attempt2PaidCount',
    attempt: 2,
    label: 'Attempt 2 · success',
    unit: 'credits',
    description:
      'Confirmed credits assigned to successful attempt 2; click to filter model usage and operations',
  },
  {
    key: 'attempt3Credits',
    countKey: 'attempt3PaidCount',
    attempt: 3,
    label: 'Attempt 3 · success',
    unit: 'credits',
    description:
      'Confirmed credits assigned to successful attempt 3; click to filter model usage and operations',
  },
];
function UsageCard({
  card,
  value,
  previous,
  count,
  unknownCredits,
  onSelect,
  selected,
}: {
  card: CardDefinition;
  value: number;
  previous: number | null;
  count?: number;
  unknownCredits?: number;
  onSelect?: () => void;
  selected?: boolean;
}) {
  const delta = previous === null ? null : value - previous;
  const percent =
    previous !== null && previous > 0 && delta !== null
      ? `${Math.abs((delta / previous) * 100).toFixed(1)}%`
      : null;
  return (
    <div className={`gu-stat${selected ? ' gu-stat-selected' : ''}`}>
      <span className="gu-stat-label">
        {onSelect ? (
          <button type="button" className="gu-card-action" onClick={onSelect} aria-pressed={selected}>
            {card.label} →
          </button>
        ) : (
          card.label
        )}
        <MetricHelp label={card.label}>{`${card.description}. ${card.key === 'failedGenerations' ? 'Green means fewer of these failures; red means more. This does not count every type of generation failure.' : 'Blue means more credits than in the prior period; amber means fewer. Credits measure usage, not money or provider cost.'}`}</MetricHelp>
      </span>
      <strong>
        {number(value)} <small>{card.unit}</small>
      </strong>
      {unknownCredits !== undefined ? (
        <div className={`gu-attribution${unknownCredits > 0 ? ' gu-attribution-unknown' : ''}`}>
          {unknownCredits > 0 ? (
            <>
              <span>
                <span aria-hidden="true">⚠ </span>
                {number(unknownCredits)} credits · Unknown attempt
              </span>
              <small>Included in the confirmed total</small>
            </>
          ) : (
            <small>All credits attributed</small>
          )}
        </div>
      ) : null}
      {count !== undefined ? (
        <small className="gu-paid-count">{number(count)} charged operations</small>
      ) : null}
      <div className="gu-change">
        <AnalyticsChange direction={changeDirection(value, previous)} preference={card.key === 'failedGenerations' ? 'lower' : 'neutral'}
          label={delta === null ? 'Period comparison unavailable' : delta === 0 ? 'No change' : `${number(Math.abs(delta))}${percent ? ` (${percent})` : ' · from zero'}`} />
        {previous !== null ? <small>Previous: {number(previous)}</small> : null}
      </div>
    </div>
  );
}

export default function GenerationUsagePage() {
  const [params, setParams] = useSearchParams();
  const query = params.toString();
  const [data, setData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [start, setStart] = useState(params.get('startDate') || recentDays(30).startDate);
  const [end, setEnd] = useState(params.get('endDate') || hktToday());
  const [user, setUser] = useState(params.get('userId') || '');
  const linkedLog = params.get('generationLogId');
  const [dateError, setDateError] = useState('');
  const updateQuery = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    next.delete('page');
    Object.entries(updates).forEach(([key, value]) => {
      if (
        value === null ||
        value === '' ||
        (value === 'all' && ['status', 'fallback', 'operationType'].includes(key))
      )
        next.delete(key);
      else next.set(key, value);
    });
    setParams(next);
  };

  const appliedStart = params.get('startDate') || recentDays(30).startDate;
  const appliedEnd = params.get('endDate') || hktToday();
  const appliedUser = params.get('userId') || '';
  const appliedStatus = params.get('status') || 'all';
  const appliedFallback = params.get('fallback') || 'all';
  const appliedOperation = params.get('operationType') || 'all';
  useEffect(() => {
    setStart(appliedStart);
    setEnd(appliedEnd);
    setDateError('');
  }, [appliedStart, appliedEnd]);
  useEffect(() => {
    setUser(appliedUser);
  }, [appliedUser]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setData(null);
    setExpanded(null);
    apiRequest<UsageData>(`/api/admin/generation-usage?${query}`, {
      signal: controller.signal,
    })
      .then((result) => {
        if (active) {
          setData(result);
          if (new URLSearchParams(query).has('generationLogId'))
            setExpanded(result.rows[0]?.reservationId || null);
        }
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : 'Failed to load generation usage');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [query, refresh]);

  const apply = (event: React.FormEvent) => {
    event.preventDefault();
    const result = validateRange({ startDate: start, endDate: end });
    if (!result.valid) {
      setDateError(result.error || 'Choose a valid date range.');
      return;
    }
    setDateError('');
    updateQuery({ startDate: start, endDate: end, generationLogId: null });
    const disclosure = event.currentTarget.closest('details');
    if (disclosure) {
      disclosure.open = false;
      disclosure.querySelector('summary')?.focus();
    }
  };
  const selectPeriod = (days: number) => {
    const period = recentDays(days);
    setStart(period.startDate);
    setEnd(period.endDate);
    setDateError('');
    updateQuery({ ...period, generationLogId: null });
  };
  const applyUser = () => updateQuery({ userId: user.trim() || null });
  const reset = () => {
    setParams({});
    const period = recentDays(30);
    setStart(period.startDate);
    setEnd(period.endDate);
    setUser('');
    setDateError('');
    if (!query) setRefresh((value) => value + 1);
  };
  const changePage = (page: number) => {
    const next = new URLSearchParams(params);
    next.set('page', String(page));
    setParams(next);
  };

  return (
    <div className="page generation-usage analytics-workspace">
      <AnalyticsHeader
        title="Generation diagnostics"
        action={
          <Link to="/generation-failed-logs">
            View failed logs <FiExternalLink />
          </Link>
        }
      />
      <AnalyticsFilterBar>
        <AnalyticsPeriodGroup
          presets={
            <AnalyticsPeriodPresets
              active={(days) =>
                appliedStart === recentDays(days).startDate && appliedEnd === hktToday() && !linkedLog
              }
              onSelect={selectPeriod}
            />
          }
        >
          <AnalyticsDateControl
            appliedStart={appliedStart}
            appliedEnd={appliedEnd}
            startDate={start}
            endDate={end}
            startMax={end}
            endMin={start}
            endMax={hktToday()}
            error={dateError}
            onStartDateChange={setStart}
            onEndDateChange={setEnd}
            onApply={apply}
          />
        </AnalyticsPeriodGroup>
        <AnalyticsFilterGroup>
          <AnalyticsSelectField
            label="Credit status"
            value={appliedStatus}
            options={[
              { value: 'all', label: 'All statuses' },
              ...['confirmed', 'reserved', 'released', 'expired', 'failed'].map((value) => ({
                value,
                label: value,
              })),
            ]}
            onChange={(status) => updateQuery({ status })}
          />
          <AnalyticsSelectField
            label="Provider fallback"
            value={appliedFallback}
            options={[
              { value: 'all', label: 'All fallbacks' },
              ...Object.entries(FALLBACK_LABELS).map(([value, label]) => ({
                value,
                label,
              })),
            ]}
            onChange={(fallback) => updateQuery({ fallback })}
          />
        </AnalyticsFilterGroup>
        <AnalyticsFilterActions>
            <AnalyticsAdvancedFilters>
              <AnalyticsTextField
                label="User ID"
                value={user}
                maxLength={200}
                onChange={setUser}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    applyUser();
                  }
                }}
                placeholder="All users"
              />
              <AnalyticsPrimaryButton onClick={applyUser}>Apply user</AnalyticsPrimaryButton>
              <AnalyticsSelectField
                label="Operation"
                value={appliedOperation}
                options={[
                  { value: 'all', label: 'All operations' },
                  ...['transform', 'blend', 'extension', 'createExtension', 'upscale', 'custom'].map((value) => ({
                    value,
                    label: value,
                  })),
                ]}
                onChange={(operationType) => updateQuery({ operationType })}
              />
            </AnalyticsAdvancedFilters>
            <AnalyticsRefreshButton onClick={() => setRefresh((value) => value + 1)} />
            <AnalyticsSecondaryButton onClick={reset}>Reset</AnalyticsSecondaryButton>
        </AnalyticsFilterActions>
      </AnalyticsFilterBar>
      {(() => {
        const chips: Array<[string, string, string]> = [];
        if (appliedUser) chips.push(['userId', 'User', appliedUser]);
        if (appliedStatus !== 'all') chips.push(['status', 'Status', appliedStatus]);
        if (appliedFallback !== 'all')
          chips.push(['fallback', 'Fallback', FALLBACK_LABELS[appliedFallback] || appliedFallback]);
        if (appliedOperation !== 'all') chips.push(['operationType', 'Operation', appliedOperation]);
        if (params.get('attempt')) chips.push(['attempt', 'Attempt', params.get('attempt')!]);
        if (params.get('modelKey')) chips.push(['modelKey', 'Model', params.get('modelKey')!]);
        return chips.length ? (
          <div className="gu-active-filters" aria-label="Applied filters">
            {chips.map(([key, label, value]) => (
              <span key={key}>
                {label}: {value}
                <button
                  type="button"
                  aria-label={`Remove ${label} filter`}
                  onClick={() => updateQuery({ [key]: null })}
                >
                  <FiX />
                </button>
              </span>
            ))}
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(params);
                chips.forEach(([key]) => next.delete(key));
                next.delete('page');
                setParams(next);
              }}
            >
              Clear filters
            </button>
          </div>
        ) : null;
      })()}
      {linkedLog ? (
        <p className="gu-scope">
          Showing linked generation log <code>{linkedLog}</code>, regardless of date. Apply dates, select a
          period, or reset to return to the period report.
        </p>
      ) : (
        <p className="gu-scope gu-period-context">
          {data?.previous ? <span>Compared with {data.previous.period.startDate} – {data.previous.period.endDate}</span> : null}
          <span>HKT</span>
          {appliedEnd === hktToday() ? <span>Today is incomplete</span> : null}
          <MetricHelp label="Report scope">All matching credit reservations, including internal accounts. Dates use operation start time in Hong Kong. Comparisons use the same filters. Missing logs can affect outcome counts.</MetricHelp>
        </p>
      )}
      <details className="gu-definitions">
        <summary>Metric definitions</summary>
        <p>Each row is one credit reservation. You can select up to 366 days. Internal accounts are included. AI outcome counts use available logs; missing logs can affect period comparisons.</p>
        <p>
          Period uses operation start time in Hong Kong. Totals cover all matching reservations, not just this
          page. Only confirmed credits count as consumed. Provider fallback means the third attempt on Vertex
          AI or official OpenAI; a second VectorEngine key is a retry. AI success does not guarantee delivery
          or a confirmed charge. Each charge is assigned once to its successful attempt; these credits are not
          provider costs.
        </p>
      </details>
      {loading ? (
        <div className="gu-message" role="status">
          Loading generation usage…
        </div>
      ) : error ? (
        <div className="gu-message gu-error" role="alert">
          {error} <button onClick={() => setRefresh((value) => value + 1)}>Retry</button>
        </div>
      ) : data ? (
        <>
          <div className="gu-summary">
            {SUMMARY_CARDS.map((card) => (
              <UsageCard
                key={card.key}
                card={card}
                value={data.summary[card.key]}
                count={card.countKey ? data.summary[card.countKey] : undefined}
                unknownCredits={
                  card.key === 'confirmedCredits' ? data.summary.unknownAttemptCredits : undefined
                }
                selected={params.get('attempt') === String(card.attempt)}
                onSelect={
                  card.attempt && !linkedLog
                    ? () => {
                        const next = new URLSearchParams(params);
                        next.delete('page');
                        if (next.get('attempt') === String(card.attempt)) next.delete('attempt');
                        else next.set('attempt', String(card.attempt));
                        setParams(next);
                      }
                    : undefined
                }
                previous={data.previous?.summary[card.key] ?? null}
              />
            ))}
          </div>
          {!linkedLog ? <section className="gu-quality" aria-labelledby="gu-quality-heading">
            <div className="gu-quality-heading">
              <div>
                <h2 id="gu-quality-heading">Provider fallback outcomes</h2>
                <p>Operation counts across all pages for the applied filters. Select an outcome to inspect its operations.</p>
              </div>
              <MetricHelp label="Provider fallback outcomes">
                These counts cover the third attempt on a fallback provider. Unknown means the available
                outcome snapshot or generation log cannot establish what happened. A retry on a second
                VectorEngine key is not a provider fallback.
              </MetricHelp>
            </div>
            <div className="gu-quality-signals">
              {FALLBACK_SIGNALS.map((signal) => (
                <button
                  key={signal.key}
                  type="button"
                  className="gu-quality-signal"
                  aria-pressed={appliedFallback === signal.filter}
                  onClick={() => updateQuery({ fallback: appliedFallback === signal.filter ? null : signal.filter })}
                >
                  <span>{signal.label}</span>
                  <strong>{number(data.summary[signal.key])}</strong>
                  <small>{signal.description}</small>
                  <span className="gu-quality-action">
                    {appliedFallback === signal.filter ? 'Clear outcome filter' : 'View operations'} →
                  </span>
                </button>
              ))}
            </div>
            <p className="gu-quality-coverage">
              Period-wide AI success rate and p50/p95 duration are unavailable with current outcome and
              timing coverage. Attempt times below apply only to the listed operations.
            </p>
          </section> : null}
          {params.get('attempt') ? (
            <p className="gu-note">
              Showing credits and operations assigned to attempt {params.get('attempt')}.{' '}
              <button
                type="button"
                className="gu-secondary"
                onClick={() => {
                  const next = new URLSearchParams(params);
                  next.delete('attempt');
                  next.delete('page');
                  setParams(next);
                }}
              >
                Clear attempt filter
              </button>
            </p>
          ) : null}
          {!linkedLog ? <ModelUsagePanel key={query} query={query} /> : null}
          <div className="gu-results-heading">
            <h2>{number(data.pagination.total)} operations</h2>
            {data.period ? (
              <span>
                {data.period.startDate} – {data.period.endDate} · HKT
              </span>
            ) : null}
          </div>
          {data.rows.length === 0 ? (
            <div className="gu-message">
              {linkedLog
                ? 'No linked credit reservation found. The log may be unavailable or may not contain a reservation ID.'
                : 'No operations match these filters.'}
            </div>
          ) : (
            <div
              className="gu-table-wrap"
              tabIndex={0}
              role="region"
              aria-label="Operations, scroll horizontally for all columns"
            >
              <table className="gu-table">
                <thead>
                  <tr>
                    <th>Started · HKT / User</th>
                    <th>Operation / Model</th>
                    <th>Credit amount / Status</th>
                    <th>AI outcome / Final provider</th>
                    <th>Provider fallback</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row) => (
                    <React.Fragment key={row.reservationId}>
                      <tr>
                        <td>
                          {dateTime(row.createdAt)}
                          <strong>{row.userName || row.userId}</strong>
                          {row.userName ? <small>{row.userId}</small> : null}
                        </td>
                        <td>
                          {row.operationType}
                          <small>
                            {row.successfulOutcome?.requestedModelId ||
                              row.generationLog?.requestModelId ||
                              row.model ||
                              row.generationLog?.requestModel ||
                              'Unknown model'}
                          </small>
                        </td>
                        <td>
                          <strong>{number(row.amount)} credits</strong>
                          <span className={`gu-badge gu-${row.status}`}>{row.status}</span>
                          {row.consumedCredits !== null ? (
                            <small>{number(row.consumedCredits)} consumed</small>
                          ) : (
                            <small>Not confirmed consumption</small>
                          )}
                        </td>
                        <td>
                          {row.successfulOutcome ? 'success' : row.generationLog?.finalStatus || 'Unknown'}
                          <small>
                            {row.successfulOutcome?.provider ||
                              row.generationLog?.successfulProvider ||
                              'No successful provider recorded'}
                          </small>
                        </td>
                        <td>
                          <span className={`gu-badge gu-${row.fallback}`}>
                            {FALLBACK_LABELS[row.fallback]}
                          </span>
                        </td>
                        <td>
                          <button
                            className="gu-secondary"
                            aria-expanded={expanded === row.reservationId}
                            aria-controls={`details-${row.reservationId}`}
                            onClick={() =>
                              setExpanded(expanded === row.reservationId ? null : row.reservationId)
                            }
                          >
                            {expanded === row.reservationId ? 'Hide' : 'Attempts'}
                          </button>
                        </td>
                      </tr>
                      {expanded === row.reservationId ? (
                        <tr>
                          <td colSpan={6}>
                            <section
                              className="gu-details"
                              id={`details-${row.reservationId}`}
                              aria-label="Generation attempts"
                            >
                              <p>
                                Reservation: <code>{row.reservationId}</code>
                                {row.generationId ? (
                                  <>
                                    {' '}
                                    · Generation: <code>{row.generationId}</code>
                                  </>
                                ) : null}
                              </p>
                              <GenerationOutcomeSummary row={row} />
                              {row.generationLog ? (
                                <>
                                  <p>
                                    Log: <code>{row.generationLog._id}</code> · Total AI time:{' '}
                                    {(row.generationLog.totalDurationMs / 1000).toFixed(1)}s
                                  </p>
                                  {row.generationLog.attempts.length ? (
                                    <ol>
                                      {row.generationLog.attempts.map((attempt, index) => (
                                        <li key={`${attempt.attemptNumber}-${index}`}>
                                          <div>
                                            <strong>
                                              Attempt {attempt.attemptNumber} · {attempt.provider}
                                            </strong>
                                            <span className={`gu-badge gu-${attempt.status}`}>
                                              {attempt.status}
                                            </span>
                                            <span>{(attempt.durationMs / 1000).toFixed(1)}s</span>
                                          </div>
                                          <p>
                                            {attempt.modelId || attempt.model}{' '}
                                            {attempt.modelId && attempt.model !== attempt.modelId
                                              ? `(${attempt.model})`
                                              : ''}
                                          </p>
                                          {attempt.errorMessage ? (
                                            <p className="gu-error-text">
                                              {attempt.errorCode ? `${attempt.errorCode}: ` : ''}
                                              {attempt.errorMessage}
                                            </p>
                                          ) : null}
                                          {attempt.requestParams ? (
                                            <details>
                                              <summary>Request parameters</summary>
                                              <pre>{JSON.stringify(attempt.requestParams, null, 2)}</pre>
                                            </details>
                                          ) : null}
                                        </li>
                                      ))}
                                    </ol>
                                  ) : (
                                    <p>No attempts recorded yet.</p>
                                  )}
                                </>
                              ) : (
                                <p>
                                  Generation log unavailable. Detailed attempt history cannot be displayed.
                                </p>
                              )}
                            </section>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination
            variant="analytics"
            currentPage={data.pagination.page}
            totalPages={data.pagination.pages}
            onPageChange={changePage}
          />
        </>
      ) : null}
    </div>
  );
}
